import { History } from 'history';
import { PATHS } from 'utils/constants';

const PREVIEW_PATH_PREFIX = '/preview/';

/**
 * True when a post-login redirect value targets the preview app. Single source of
 * truth for "is this a preview target" — used by the resolver's hard branch and by
 * Login.tsx's legacy accountId derivation, which must skip preview values.
 */
export const isPreviewRedirect = (value?: string | null): boolean =>
  !!value && value.startsWith(PREVIEW_PATH_PREFIX);

export type PostLoginNavigation = {
  /** true → full page load (window.location.assign); false → SPA route (history push/replace). */
  hard: boolean;
  url: string;
};

/**
 * Decides how to navigate to the post-login target. The preview app (matched by a
 * literal /preview/ prefix) and the design app are served by separate bundles behind
 * the same origin, so an SPA route change lands on learnops' catch-all shell instead
 * of those apps and they need a hard navigation. Both targets are intentionally
 * absent from App.tsx's <Route> table — they fall through to the PATHS.ROOT
 * catch-all — so if a real learnops route is ever added for one of them, drop it
 * from here too. Keep in sync with
 * preview/app/js/errors/NotLoggedInError/NotLoggedInError.jsx, which builds
 * /login?redirect=/preview/<token> links relying on the hard branch (CGP-206).
 *
 * The strict /preview/ prefix check runs before the looser includes(ELEMENTS) so a
 * preview query string containing "elements" cannot be misrouted to the design app.
 * The elements match is a bare substring test (PATHS.ELEMENTS is 'elements', with no
 * leading slash) rather than a prefix check because legacy design links are
 * hash-routed — useAuthentication.ts forwards window.location.hash verbatim, so
 * values like '#/elements/<id>' must still reach the design app.
 *
 * No caller sanitizes the value first and none should: useAuthentication.ts builds
 * bare '#/foo' redirects and the legacy SSO links append '&<otherParams>', both of
 * which a strict internal-path guard (e.g. sanitizeInternalPath in
 * utils/logoutRedirectStorage) would reject, silently collapsing the post-login
 * target to '/'. This resolver is instead safe by construction:
 * (a) the preview branch requires the literal /preview/ prefix, so anything it hands
 *     to window.location.assign is same-origin and root-relative;
 * (b) the elements branch always emits designURL + the value — every environment
 *     config sets designURL to a non-empty absolute URL on the design origin, and
 *     the branch below refuses to hard-navigate without one — so hostile text can
 *     only ever become an inert path segment on that origin;
 * (c) the soft branch feeds history.push/replace, which treats any string as a
 *     same-origin path and cannot leave the origin.
 *
 * The elements branch's double slash (designURL ends in '/', so '/elements/x' yields
 * '…/design//elements/x') is preserved legacy behaviour from Login.tsx and is pinned
 * by test — every environment's server normalises it, so do not "fix" it.
 */
export const resolvePostLoginNavigation = (
  redirectUrl: string | null | undefined,
  designURL: string
): PostLoginNavigation => {
  if (!redirectUrl) {
    return { hard: false, url: PATHS.ROOT };
  }
  if (isPreviewRedirect(redirectUrl)) {
    return { hard: true, url: redirectUrl };
  }
  if (designURL && redirectUrl.includes(PATHS.ELEMENTS)) {
    return { hard: true, url: `${designURL}${redirectUrl}` };
  }
  return { hard: false, url: redirectUrl };
};

/**
 * Performs the post-login navigation: hard targets get window.location.assign, and
 * the mutual exclusion with the SPA navigation is structural — callers cannot
 * reintroduce the "hard-navigate and then also SPA-navigate" fall-through this
 * module exists to prevent (CGP-206). Run any must-happen-before-unload work
 * (e.g. clearPostTimeoutRedirect) BEFORE calling this.
 */
export const navigateAfterLogin = (
  history: Pick<History, 'push' | 'replace'>,
  redirectUrl: string | null | undefined,
  designURL: string,
  options: { replace?: boolean } = {}
): void => {
  const { hard, url } = resolvePostLoginNavigation(redirectUrl, designURL);
  if (hard) {
    window.location.assign(url);
  } else if (options.replace) {
    history.replace(url);
  } else {
    history.push(url);
  }
};
